/**
 * Sport placement — the little state machine between "I designed an activity"
 * and "it lives on this day".
 *
 * The sport composer no longer opens from a day, it opens from the header, so
 * a finished draft has no date yet. It is parked as a *pending placement*
 * while the calendar waits for a tap. These helpers are the pure part of that
 * step: validating the parked draft and wording the prompt.
 */

import { REPEAT_DAILY, REPEAT_ONCE, REPEAT_WEEKLY } from './habits.js';
import { WEEKDAY_LABELS, parseDateKey, weekdayIndex } from './date.js';

/**
 * Is this draft complete enough to be parked and placed?
 *
 * Deliberately shallow: the blueprint itself is validated once more by
 * `normalizeSportConfig` on the way into storage. This only guards the step in
 * between, so the calendar never enters picking mode for nothing.
 *
 * @param {{name?: string, sport?: object}|null|undefined} draft
 * @returns {boolean}
 */
export function isPlaceableDraft(draft) {
  return Boolean(draft && String(draft.name || '').trim() && draft.sport);
}

/**
 * The banner headline while the calendar waits for a day.
 * @param {{name?: string}|null|undefined} draft
 * @returns {string}
 */
export function placementPrompt(draft) {
  const name = String(draft?.name || '').trim();
  return name ? `tap a day for “${name}”` : 'tap a day';
}

/**
 * What the tapped day will mean, spelled out before the tap rather than after.
 *
 * Weekly and daily anchor to whatever day is chosen (see `isBaseOccurrence`),
 * which is easy to get wrong if nobody says so.
 *
 * @param {string} repeat one of the REPEAT_* values
 * @returns {string}
 */
export function placementHint(repeat) {
  if (repeat === REPEAT_WEEKLY) return 'it will repeat every week on that weekday';
  if (repeat === REPEAT_DAILY) return 'it will repeat every day from then on';
  return 'it will sit on that one day';
}

/**
 * Confirmation wording once a day is known, e.g. "every Tue from 2026-08-11".
 * Used for the screen-reader label of each day while picking, so tapping is
 * not a guess for anyone navigating without sight of the highlight.
 *
 * @param {string} repeat
 * @param {string} dateKey `YYYY-MM-DD`
 * @returns {string}
 */
export function placementSummary(repeat, dateKey) {
  if (!dateKey) return '';
  const weekday = WEEKDAY_LABELS[weekdayIndex(parseDateKey(dateKey))];
  if (repeat === REPEAT_WEEKLY) return `every ${weekday} from ${dateKey}`;
  if (repeat === REPEAT_DAILY) return `every day from ${dateKey}`;
  return `on ${weekday} ${dateKey}`;
}

/** Re-exported so callers can name the default without reaching into habits.js. */
export { REPEAT_ONCE };
